<template>
  <div>
    <div class="header">
      <title-vue :title="`Fase do dia ${formatDate($route.params.date)}`" />
      <status-badge-vue :status="phase.status" />
    </div>

    <v-skeleton-loader
      v-if="!!loading"
      tile
      loading
      class="mx-auto"
      type="card"
      elevation="{5}"
      boilerplate
    />
    <div v-else>
      <div class="header">
        <v-row class="pa-4">
          <v-col cols="2 mr-8">
            <card-resume-vue
              backgroundColor="#5A41F3"
              title1="Total de usuários no dia"
              :title2="String(phase.usersQuantity)"
            />
          </v-col>
          <v-col cols="2">
            <card-resume-vue
              backgroundColor="#24A859"
              title1="Valor total"
              :title2="formatPrice(phase.totalAmmount)"
            />
          </v-col>
        </v-row>
        <v-btn
          :disabled="phase.status == 'PAID' || loading || !isDistributionValid"
          :dark="phase.status != 'PAID' || !!isDistributionValid"
          color="#e5183d"
          @click="openDialog = true"
        >
          Liberar distribuição
        </v-btn>
      </div>
      <v-card>
        <v-data-table
          :headers="headers"
          :items-per-page="23"
          :items="phase.distributions"
          :footer-props="{ 'items-per-page-text': 'fases por página' }"
          :options="{ pagination: false }"
          class="elevation-0"
        >
          <template v-slot:body="{ items }">
            <tbody>
              <tr v-for="item in items" :key="item.id">
                <td class="d-block d-sm-table-cell">{{ item.missionId }}</td>
                <td class="d-block d-sm-table-cell">
                  {{ item.usersQuantity }}
                </td>
                <td class="d-block d-sm-table-cell">
                  <v-text-field
                    v-money="money"
                    v-model="item.distributionPercent"
                    :readonly="item.usersQuantity <= 0"
                    dense
                    hide-details
                    @input="recalculateAmountsForRow(item)"
                  />
                </td>
                <td class="d-block d-sm-table-cell">
                  {{ formatPrice(item.individualAmount) }}
                </td>
                <td class="d-block d-sm-table-cell">
                  {{ formatPrice(item.missionAmount) }}
                </td>
                <td class="d-block d-sm-table-cell">
                  <status-badge-vue :status="item.status || 'PENDING'" />
                </td>
                <td class="d-block d-sm-table-cell">
                  <v-btn text small color="#e5183d" @click="openConsumersDialog(item)">
                    Detalhes
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </template>
        </v-data-table>
      </v-card>
    </div>
    <v-dialog
      v-model="openDialog"
      width="600"
      persistent
      transition="dialog-top-transition"
    >
      <v-card>
        <v-card-title></v-card-title>
        <v-card-text>
          <h2 class="black--text">
            Deseja realmente liberar o pagamento do sistema de fases do dia
            {{ formatDate($route.params.date) }}?
          </h2>
        </v-card-text>
        <v-card-actions class="d-flex justify-space-between">
          <v-btn elevation="0" text @click="openDialog = false"> Não </v-btn>
          <v-btn
            elevation="0"
            dark
            color="#e5183d"
            @click="handlePaymentPhases"
          >
            Sim
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="consumersDialog"
      max-width="920"
      scrollable
      @click:outside="closeConsumersDialog"
    >
      <v-card>
        <v-card-title class="headline">
          Consumidores — {{ consumersMissionTitle }}
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-0">
          <v-skeleton-loader v-if="consumersLoading" type="table" />
          <v-data-table
            v-else
            :headers="consumerHeaders"
            :items="consumers"
            :items-per-page="15"
            class="elevation-0"
            no-data-text="Nenhum consumidor nesta missão nesta data"
          >
            <template v-slot:body="{ items }">
              <tbody>
                <tr v-for="row in items" :key="row.userId">
                  <td class="d-block d-sm-table-cell text-truncate" style="max-width: 220px">
                    {{ row.userId }}
                  </td>
                  <td
                    class="d-block d-sm-table-cell text-truncate"
                    style="max-width: 240px"
                    :title="row.name || undefined"
                  >
                    {{ row.name ? row.name : "—" }}
                  </td>
                  <td class="d-block d-sm-table-cell">
                    {{ row.document ? formatDocument(row.document) : "—" }}
                  </td>
                  <td class="d-block d-sm-table-cell">
                    {{ formatDateTime(row.activatedAt) }}
                  </td>
                </tr>
              </tbody>
            </template>
          </v-data-table>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeConsumersDialog">Fechar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import StatusBadgeVue from "@/components/statusBadge/StatusBadge.vue";
import CardResumeVue from "@/components/cardResume/CardResume.vue";

import TitleVue from "@/components/title/Title.vue";
import {
  DistributionConsumerType,
  PhaseDetailType,
  PhaseDistributionType,
} from "@/types/phase/Phases";
import { Status } from "@/types/phase/Status";
import { GetPhase } from "@/services/phases/GetPhase";
import { PaymentPhases } from "@/services/phases/PaymentPhases";
import { UpdateDistribution } from "@/services/phases/UpdateDistribution";
import { GetDistributionConsumers } from "@/services/phases/GetDistributionConsumers";
import { format, parseISO } from "date-fns";

/** TEMPORÁRIO: true = só loga o contrato no console; não chama a API. Voltar para false antes de liberar de verdade. */
const TEMP_ONLY_LOG_DISTRIBUICAO_CASHBACK = false;

interface SortItem {
  name: string;
  phone: string;
  email: string;
  document: string;
}

export default defineComponent({
  components: { StatusBadgeVue, CardResumeVue, TitleVue },

  data() {
    return {
      mobileBreakpoint: 600,
      loading: true,
      showModal: false,
      showModalRemove: false,
      openDialog: false,
      consumersDialog: false,
      consumersLoading: false,
      consumers: [] as DistributionConsumerType[],
      consumersMissionTitle: "",
      consumerHeaders: [
        { text: "ID do usuário", value: "userId" },
        { text: "Nome", value: "name" },
        { text: "Documento", value: "document" },
        { text: "Ativação neste dia", value: "activatedAt" },
      ],
      phase: {
        distributions: [] as PhaseDistributionType[],
        status: null as Status,
        totalAmmount: 0,
        usersQuantity: 0,
      } as PhaseDetailType,
      search: "",
      sortBy: [] as ReadonlyArray<SortItem>,
      sortDesc: false,
      noDataText: "Nenhum dado disponível",
      headers: [
        {
          text: "Fase",
          value: "phase",
        },
        { text: "Quantidade de usuários", value: "usersQuantity" },
        {
          text: "Percentual da missão",
          value: "distributions.distributionPercent",
        },
        { text: "Total individual", value: "individualAmount" },
        { text: "Total da missão", value: "totalAmount" },
        { text: "Status", value: "status" },
        { text: "Detalhes", value: "details", sortable: false },
      ],
      money: {
        decimal: ",",
        thousands: ".",
        suffix: "%",
        precision: 2,
        masked: false /* doesn't work with directive */,
      },
    };
  },
  methods: {
    parsePercentValue(raw: string | number | undefined | null): number {
      if (raw === null || raw === undefined) {
        return 0;
      }

      const normalized = String(raw)
        .replace("%", "")
        .replace(/\s/g, "")
        .replace(",", ".");
      const parsed = parseFloat(normalized);
      return Number.isFinite(parsed) ? parsed : 0;
    },

    roundMoney(value: number): number {
      return Math.round(value * 10000) / 10000;
    },

    recalculateAmountsForRow(item: PhaseDistributionType) {
      const pool = Number(this.phase.totalAmmount) || 0;
      const percent = this.parsePercentValue(item.distributionPercent);
      const missionAmount = this.roundMoney(pool * (percent / 100));
      const users = Number(item.usersQuantity) || 0;
      const individualAmount =
        users > 0 ? this.roundMoney(missionAmount / users) : 0;

      item.missionAmount = missionAmount;
      item.totalAmount = missionAmount;
      item.individualAmount = individualAmount;
    },

    recalculateAllDistributionAmounts() {
      this.phase.distributions.forEach((row) => {
        this.recalculateAmountsForRow(row);
      });
    },

    buildDistributionUpdatePayload(): { id: string; distributionPercent: number }[] {
      return this.phase.distributions.map(({ id, distributionPercent }) => ({
        id,
        distributionPercent: this.parsePercentValue(distributionPercent),
      }));
    },

    async handleLoadPhase() {
      this.loading = true;
      const getPhase = new GetPhase();
      const phase = await getPhase.execute(this.$route.params.date);
      this.phase = phase;
      this.recalculateAllDistributionAmounts();
      this.loading = false;
    },

    async handlePaymentPhases() {
      this.loading = true;
      try {
        const distributionPayload = this.buildDistributionUpdatePayload();
        const distributionDate = this.$route.params.date as string;

        if (TEMP_ONLY_LOG_DISTRIBUICAO_CASHBACK) {
          const patchBody = { distributions: distributionPayload };
          const paymentBody = { distributionDate };
          const contract = {
            patch: {
              path: "/distributions/update",
              body: patchBody,
            },
            post: {
              path: "/distributions/payment",
              body: paymentBody,
            },
          };
          console.log(
            "[TEMP] Liberar distribuição — contrato que seria enviado à API:",
            JSON.stringify(contract, null, 2)
          );
          console.log("[TEMP] Contrato (objeto):", contract);
          this.$store.dispatch("showSnack", {
            message:
              "Modo temporário: contrato no console; nada foi enviado à API.",
            color: "orange darken-2",
          });
          this.loading = false;
          return;
        }

        const updateDistribution = new UpdateDistribution();
        await updateDistribution.execute(distributionPayload);

        const paymentPhase = new PaymentPhases();
        const res = await paymentPhase.execute(this.$route.params.date as string);
        if (res.status === 201) {
          this.$store.dispatch("showSnack", {
            message: "Sistema de fases pago com sucesso",
            color: "green darken-1",
          });
        } else {
          this.$store.dispatch("showSnack", {
            message: "Erro ao pagar o sistema de fases",
            color: "red",
          });
        }
        await this.handleLoadPhase();
      } catch {
        this.$store.dispatch("showSnack", {
          message: "Falha ao atualizar distribuição ou ao processar pagamento.",
          color: "red",
        });
        this.loading = false;
      } finally {
        this.openDialog = false;
      }
    },
    formatDocument(document: string): string {
      if (!document) return "";
      if (document.length === 11) {
        return document.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      }
      if (document.length === 14) {
        return document.replace(
          /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
          "$1.$2.$3/$4-$5"
        );
      }
      return document;
    },

    formatDateTime(value: string): string {
      if (!value) return "";
      return format(parseISO(value), "dd/MM/yyyy HH:mm");
    },

    closeConsumersDialog() {
      this.consumersDialog = false;
      this.consumers = [];
      this.consumersMissionTitle = "";
    },

    async openConsumersDialog(item: PhaseDistributionType) {
      const dateParam = this.$route.params.date as string;
      this.consumersMissionTitle = `Missão ${item.missionId} — ${this.formatDate(dateParam)}`;
      this.consumersDialog = true;
      this.consumersLoading = true;
      this.consumers = [];
      try {
        const svc = new GetDistributionConsumers();
        const { consumers } = await svc.execute(dateParam, item.missionId);
        this.consumers = consumers ?? [];
      } catch {
        this.$store.dispatch("showSnack", {
          message: "Não foi possível carregar os consumidores desta missão.",
          color: "red",
        });
      } finally {
        this.consumersLoading = false;
      }
    },

    formatDate: (date: string): string => {
      const dateFormated = format(parseISO(date), "dd/MM/yyyy");
      return dateFormated;
    },

    formatPrice(value: number): string {
      if (!value && value !== 0) return "";
      const formattedValue = new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 4,
      }).format(value);
      return formattedValue;
    },
  },
  computed: {
    isDistributionValid(): boolean {
      const totalPercent = this.phase.distributions.reduce((sum, item) => {
        const formattedPercent = Number(
          String(item.distributionPercent).replace("%", "").replace(",", ".")
        );
        return sum + (isNaN(formattedPercent) ? 0 : formattedPercent);
      }, 0);
      const roundedTotal = Math.round(totalPercent * 100) / 100;
      return roundedTotal > 0 && roundedTotal <= 100;
    },
  },
  mounted() {
    if (this.$route.params.date) {
      this.handleLoadPhase();
    } else {
      this.$router.back();
    }
  },
});
</script>

<style scoped lang="scss">
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

tbody tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, 0.05);
}

.v-data-footer {
  display: none !important;
}
</style>
